import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import {
    SplitSquareHorizontal, X, FileText, Users, Globe, StickyNote,
    BookOpen, ChevronDown, GripVertical
} from 'lucide-react';
import 'react-quill-new/dist/quill.snow.css';

const SplitScreenView = ({
    isOpen,
    onClose,
    // Left pane (editor)
    editorContent,
    onContentChange,
    chapterTitle,
    lineSpacing,
    quillModules,
    // Reference data
    chapters,
    characters,
    locations,
    notes
}) => {
    const [rightPaneContent, setRightPaneContent] = useState('chapters');
    const [selectedReference, setSelectedReference] = useState(null);
    const [splitPosition, setSplitPosition] = useState(60); // percentage
    const containerRef = useRef(null);
    const isDragging = useRef(false);

    // Handle resize drag
    const handleMouseDown = (e) => {
        isDragging.current = true;
        e.preventDefault();
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging.current || !containerRef.current) return;
            
            const container = containerRef.current;
            const rect = container.getBoundingClientRect();
            const newPosition = ((e.clientX - rect.left) / rect.width) * 100;
            
            // Constrain between 30% and 70%
            setSplitPosition(Math.min(70, Math.max(30, newPosition)));
        };

        const handleMouseUp = () => {
            isDragging.current = false;
        };

        if (isOpen) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isOpen]);

    // Keyboard shortcut to close
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    const referenceTypes = [
        { id: 'chapters', label: 'Chapters', icon: BookOpen, count: chapters?.length || 0 },
        { id: 'characters', label: 'Characters', icon: Users, count: characters?.length || 0 },
        { id: 'locations', label: 'Locations', icon: Globe, count: locations?.length || 0 },
        { id: 'notes', label: 'Notes', icon: StickyNote, count: notes?.length || 0 },
    ];

    const renderReferenceContent = () => {
        switch (rightPaneContent) {
            case 'chapters':
                return (
                    <div className="space-y-2">
                        {chapters?.map((ch, idx) => (
                            <button
                                key={ch.id}
                                className={`w-full p-3 text-left rounded-lg border transition-colors ${
                                    selectedReference?.id === ch.id 
                                        ? 'border-accent bg-accent/5' 
                                        : 'border-border hover:border-accent/50'
                                }`}
                                onClick={() => setSelectedReference(ch)}
                            >
                                <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs">Ch {idx + 1}</Badge>
                                    <span className="font-medium text-sm truncate">{ch.title}</span>
                                </div>
                                {ch.synopsis && (
                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                        {ch.synopsis}
                                    </p>
                                )}
                                <p className="text-xs text-muted-foreground mt-1">
                                    {(ch.word_count || 0).toLocaleString()} words
                                </p>
                            </button>
                        ))}
                    </div>
                );

            case 'characters':
                return (
                    <div className="space-y-2">
                        {characters?.map(char => (
                            <button
                                key={char.id}
                                className={`w-full p-3 text-left rounded-lg border transition-colors ${
                                    selectedReference?.id === char.id 
                                        ? 'border-accent bg-accent/5' 
                                        : 'border-border hover:border-accent/50'
                                }`}
                                onClick={() => setSelectedReference(char)}
                            >
                                <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs">{char.role}</Badge>
                                    <span className="font-medium text-sm">{char.name}</span>
                                </div>
                                {char.description && (
                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                        {char.description}
                                    </p>
                                )}
                            </button>
                        ))}
                    </div>
                );

            case 'locations':
                return (
                    <div className="space-y-2">
                        {locations?.map(loc => (
                            <button
                                key={loc.id}
                                className={`w-full p-3 text-left rounded-lg border transition-colors ${
                                    selectedReference?.id === loc.id 
                                        ? 'border-accent bg-accent/5' 
                                        : 'border-border hover:border-accent/50'
                                }`}
                                onClick={() => setSelectedReference(loc)}
                            >
                                <span className="font-medium text-sm">{loc.name}</span>
                                {loc.description && (
                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                        {loc.description}
                                    </p>
                                )}
                            </button>
                        ))}
                    </div>
                );

            case 'notes':
                return (
                    <div className="space-y-2">
                        {notes?.map(note => (
                            <button
                                key={note.id}
                                className={`w-full p-3 text-left rounded-lg border transition-colors ${
                                    selectedReference?.id === note.id 
                                        ? 'border-accent bg-accent/5' 
                                        : 'border-border hover:border-accent/50'
                                }`}
                                onClick={() => setSelectedReference(note)}
                            >
                                <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs">{note.note_type}</Badge>
                                    <span className="font-medium text-sm truncate">{note.title}</span>
                                </div>
                                {note.content && (
                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                        {note.content}
                                    </p>
                                )}
                            </button>
                        ))}
                    </div>
                );

            default:
                return null;
        }
    };

    const renderDetailView = () => {
        if (!selectedReference) {
            return (
                <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Select an item to view details</p>
                </div>
            );
        }

        // Render based on reference type
        if (rightPaneContent === 'chapters') {
            return (
                <div>
                    <h3 className="font-serif font-semibold text-lg mb-2">{selectedReference.title}</h3>
                    <Badge variant="outline" className="mb-3">
                        {(selectedReference.word_count || 0).toLocaleString()} words
                    </Badge>
                    {selectedReference.synopsis && (
                        <div className="mb-4">
                            <h4 className="text-xs font-semibold text-muted-foreground mb-1">Synopsis</h4>
                            <p className="text-sm">{selectedReference.synopsis}</p>
                        </div>
                    )}
                    {selectedReference.content && (
                        <div>
                            <h4 className="text-xs font-semibold text-muted-foreground mb-1">Content Preview</h4>
                            <div 
                                className="text-sm prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: selectedReference.content?.substring(0, 1000) }}
                            />
                        </div>
                    )}
                </div>
            );
        }

        if (rightPaneContent === 'characters') {
            return (
                <div>
                    <h3 className="font-serif font-semibold text-lg mb-2">{selectedReference.name}</h3>
                    <Badge variant="outline" className="mb-3">{selectedReference.role}</Badge>
                    {selectedReference.description && (
                        <div className="mb-3">
                            <h4 className="text-xs font-semibold text-muted-foreground mb-1">Description</h4>
                            <p className="text-sm">{selectedReference.description}</p>
                        </div>
                    )}
                    {selectedReference.backstory && (
                        <div className="mb-3">
                            <h4 className="text-xs font-semibold text-muted-foreground mb-1">Backstory</h4>
                            <p className="text-sm">{selectedReference.backstory}</p>
                        </div>
                    )}
                    {selectedReference.goals && (
                        <div className="mb-3">
                            <h4 className="text-xs font-semibold text-muted-foreground mb-1">Goals</h4>
                            <p className="text-sm">{selectedReference.goals}</p>
                        </div>
                    )}
                    {selectedReference.flaws && (
                        <div className="mb-3">
                            <h4 className="text-xs font-semibold text-muted-foreground mb-1">Flaws</h4>
                            <p className="text-sm">{selectedReference.flaws}</p>
                        </div>
                    )}
                </div>
            );
        }

        if (rightPaneContent === 'locations') {
            return (
                <div>
                    <h3 className="font-serif font-semibold text-lg mb-2">{selectedReference.name}</h3>
                    {selectedReference.description && (
                        <div className="mb-3">
                            <h4 className="text-xs font-semibold text-muted-foreground mb-1">Description</h4>
                            <p className="text-sm">{selectedReference.description}</p>
                        </div>
                    )}
                    {selectedReference.history && (
                        <div className="mb-3">
                            <h4 className="text-xs font-semibold text-muted-foreground mb-1">History</h4>
                            <p className="text-sm">{selectedReference.history}</p>
                        </div>
                    )}
                </div>
            );
        }

        if (rightPaneContent === 'notes') {
            return (
                <div>
                    <h3 className="font-serif font-semibold text-lg mb-2">{selectedReference.title}</h3>
                    <Badge variant="outline" className="mb-3">{selectedReference.note_type}</Badge>
                    {selectedReference.content && (
                        <p className="text-sm whitespace-pre-wrap">{selectedReference.content}</p>
                    )}
                </div>
            );
        }

        return null;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-40 bg-background" ref={containerRef}>
            {/* Header */}
            <div className="h-12 border-b flex items-center justify-between px-4">
                <div className="flex items-center gap-2">
                    <SplitSquareHorizontal className="h-5 w-5 text-accent" />
                    <span className="font-semibold">Split Screen View</span>
                </div>
                <Button variant="ghost" size="sm" onClick={onClose}>
                    <X className="h-4 w-4 mr-2" />
                    Close Split View
                </Button>
            </div>

            <div className="flex h-[calc(100%-3rem)]">
                {/* Left Pane - Editor */}
                <div 
                    className="overflow-hidden border-r"
                    style={{ width: `${splitPosition}%` }}
                >
                    <div className="h-full flex flex-col">
                        <div className="px-4 py-2 border-b bg-muted/50">
                            <h2 className="font-serif font-semibold">{chapterTitle}</h2>
                        </div>
                        <div className={`flex-1 overflow-auto p-4 line-spacing-${lineSpacing}`}>
                            <ReactQuill
                                theme="snow"
                                value={editorContent}
                                onChange={onContentChange}
                                modules={quillModules}
                                className="editor-content h-full"
                            />
                        </div>
                    </div>
                </div>

                {/* Resize Handle */}
                <div
                    className="w-1 bg-border hover:bg-accent cursor-col-resize flex items-center justify-center group"
                    onMouseDown={handleMouseDown}
                >
                    <div className="w-4 h-8 rounded bg-muted flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                    </div>
                </div>

                {/* Right Pane - Reference */}
                <div 
                    className="overflow-hidden"
                    style={{ width: `${100 - splitPosition}%` }}
                >
                    <div className="h-full flex flex-col">
                        {/* Reference Type Selector */}
                        <div className="px-4 py-2 border-b bg-muted/50">
                            <div className="flex items-center gap-2">
                                {referenceTypes.map(type => (
                                    <button
                                        key={type.id}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors ${
                                            rightPaneContent === type.id
                                                ? 'bg-accent text-accent-foreground'
                                                : 'bg-muted hover:bg-muted/80'
                                        }`}
                                        onClick={() => {
                                            setRightPaneContent(type.id);
                                            setSelectedReference(null);
                                        }}
                                    >
                                        <type.icon className="h-3 w-3" />
                                        {type.label}
                                        <Badge variant="secondary" className="text-xs ml-1">
                                            {type.count}
                                        </Badge>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Reference Content */}
                        <div className="flex-1 flex">
                            {/* List */}
                            <ScrollArea className="w-1/2 border-r p-3">
                                {renderReferenceContent()}
                            </ScrollArea>

                            {/* Detail View */}
                            <ScrollArea className="w-1/2 p-4">
                                {renderDetailView()}
                            </ScrollArea>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SplitScreenView;
